import { useEffect, useState } from 'react';
import { fetchAicaFinancialMetrics, FinancialMetricsData } from './financialMetricsApi';
import { computeDelta, DisplayMetric, formatMetricValue, KPI_METRIC_CONFIG, MetricTrend, SPARKLINE_METRIC_CONFIG } from './constants';

interface UseFinancialMetricsResult {
    loading: boolean;
    fyLabel: string;
    kpiMetrics: DisplayMetric[];
    sparklineMetrics: DisplayMetric[];
}

export function useFinancialMetrics(orgId?: string): UseFinancialMetricsResult {
    const getYearHeaders = (data: FinancialMetricsData) => data.headers.filter((h) => h.type);

    const buildTrendSparkline = (trend: MetricTrend | null) => {
        if (trend === 'up') return [{ value: 0 }, { value: 1 }];
        if (trend === 'down') return [{ value: 1 }, { value: 0 }];
        return [{ value: 0.5 }, { value: 0.5 }];
    };

    const toDisplayMetric = (
        config: { metricName: string; label: string },
        data: FinancialMetricsData,
        currentKey: string,
        previousKey: string,
        periodLabel: string,
    ): DisplayMetric | null => {
        const row = data.rows.find((r) => r.metricName === config.metricName);
        if (!row) return null;

        const current = row[currentKey] as number | null | undefined;
        const previous = row[previousKey] as number | null | undefined;
        const { delta, trend } = computeDelta(current, previous);

        return {
            key: config.metricName,
            label: config.label,
            value: formatMetricValue(current, row.unit),
            delta,
            trend,
            sparkline: buildTrendSparkline(trend),
            periodLabel,
        };
    };

    const [data, setData] = useState<FinancialMetricsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orgId) return;
        let active = true;
        setLoading(true);
        fetchAicaFinancialMetrics(orgId)
            .then((result) => active && setData(result))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false));
        return () => {
            active = false;
        };
    }, [orgId]);

    if (!data) {
        return { loading, fyLabel: '', kpiMetrics: [], sparklineMetrics: [] };
    }

    const yearHeaders = getYearHeaders(data);
    // The current/last year of the company's data is the last year column, and the
    // pill compares it against the year before it — both derived from the actual
    // columns rather than `latestConsolidatedFY`.
    const lastHeader = yearHeaders[yearHeaders.length - 1];
    const previousHeader = yearHeaders[yearHeaders.length - 2];
    const currentKey = lastHeader?.key ?? '';
    const previousKey = previousHeader?.key ?? '';

    // Header shows the company's last/current data year, e.g. "FY'25-26".
    const fyLabel = lastHeader?.name ?? '';
    const periodLabel = fyLabel;

    const transform = (configs: { metricName: string; label: string }[]) =>
        configs
            .map((config) => toDisplayMetric(config, data, currentKey, previousKey, periodLabel))
            .filter((m): m is DisplayMetric => m !== null);

    return {
        loading,
        fyLabel,
        kpiMetrics: transform(KPI_METRIC_CONFIG),
        sparklineMetrics: transform(SPARKLINE_METRIC_CONFIG),
    };
}
